/**
 * ActiveCallReturnBar
 *
 * Global "tap to return to call" chip - mounted once at the app root.
 * Renders when a native SIP call is live (connecting/ringing/connected)
 * AND the user is NOT already on the call surface (/connect-portal/dial).
 *
 * Mirrors the iOS / WhatsApp standard: navigating away from the call screen
 * keeps the call running and exposes a persistent, tappable indicator that
 * deep-links back to the full call UI. Without this, tapping "Back" during a
 * live call hides the screen with no way to return short of ending the call.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone } from "lucide-react";
import { useNativeSipCallContext } from "@/contexts/NativeSipCallContext";
import { formatCallDuration } from "@/components/connect/_call-shared/useCallDuration";
import { hapticTap } from "@/lib/native-haptics";

const CALL_ROUTE = "/connect-portal/dial";

export function ActiveCallReturnBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { callStatus, isCallActive, duration } = useNativeSipCallContext();

  const onCallRoute = location.pathname.startsWith(CALL_ROUTE);
  const visible = isCallActive && !onCallRoute;

  const label =
    callStatus === "connected"
      ? formatCallDuration(duration ?? 0)
      : callStatus === "ringing"
      ? "Ringing…"
      : "Connecting…";

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 420, damping: 32 }}
          onClick={() => {
            hapticTap();
            navigate(CALL_ROUTE);
          }}
          aria-label="Return to active call"
          className="fixed left-0 right-0 z-[60] flex items-center justify-center gap-2 bg-emerald-600 active:bg-emerald-700 text-white text-[13px] font-semibold shadow-md"
          style={{
            top: 0,
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 6px)",
            paddingBottom: 6,
          }}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
          </span>
          <Phone className="h-3.5 w-3.5" />
          <span className="tabular-nums">{label}</span>
          <span className="opacity-80">· Tap to return</span>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
